import { useEffect, useState } from "react";
import { Button } from "react-aria-components";
import { TableVirtuoso } from "react-virtuoso";
import { httpPost } from "../../../../../modules/Backend";
import { IFilter } from "../../../../../hooks/UseFilters";
import ConfirmModal, { ConfirmType } from "../../../Modal/ConfirmModal";
import { modalSignal } from "../../../Modal/ModalContainer";
import { HttpStatusCode } from "axios";
import { globalSubmittingSignal } from "../../Layout";
import { useSignals } from "@preact/signals-react/runtime";
import IconPlus from "../../../../../assets/icons/icon-plus-blue.svg";
import IconSearch from "../../../../../assets/icons/icon-search.svg";
import IconRefresh from "../../../../../assets/icons/icon-refresh.svg";
import AdobeTextField from "../../../../AdobeAriaLib/AdobeTextField";
import { capitalizeFirstLetter } from "../../../../../helpers/StringHelper";
import { SingularLowerCase } from "../../../../../helpers/EntityNameHelper";
import { EntityNames } from "../../../../../enums/EntityNames";
import { FilterMatchType } from "../../../../../enums/FilterMatchType";
import { PropertyNames } from "../../../../../enums/PropertyNames";
import { PropertyTypes } from "../../../../../enums/PropertyTypes";

interface ISidePanelListProps<T> {
    id: string;
    entityName: string;
    searchLabel?: string | undefined;
    onAddNewClicked?: Function | undefined;
    editFunction?: ((id: string, entityName?: string) => void) | undefined;
    itemData?: T;
    renderRowContent: (item: T, edit: ((id: string, entityName?: string) => void) | undefined, remove: ((item: T) => void) | undefined) => JSX.Element;
    searchFilter?: (searchValue?: string) => IFilter;
    displayColumns: JSX.Element;
    itemDisplayName?: (item: T) => string;
    fixedHeight?: number;
    take?: number;
    loadingRowCount?: number;
    displayName?: string | undefined;
    headerWidget?: (listItems: T[]) => JSX.Element | undefined;
    informationBarWidget?: JSX.Element | undefined;
    listWidget?: JSX.Element | undefined;
    isSmall?: boolean | undefined,
    onInitialListLoad?: (listItems: T[]) => void | undefined;
    prePopulatedItems?: T[] | undefined,
    onItemRemoved?: ((item: T) => void),
    canAdd?: boolean | undefined,
    canEdit?: boolean | undefined,
    canDelete?: boolean | undefined,
    url?: string | undefined,
    itemsToAdd?: T[] | undefined

}

export default function SidePanelList<T>({
    id,
    entityName,
    searchLabel,
    onAddNewClicked,
    editFunction,
    itemData,
    renderRowContent,
    searchFilter,
    displayColumns,
    itemDisplayName,
    fixedHeight = 275,
    take = 1000,
    loadingRowCount = 3,
    displayName,
    listWidget,
    headerWidget,
    isSmall,
    onInitialListLoad,
    prePopulatedItems,
    onItemRemoved,
    informationBarWidget,
    canAdd,
    canEdit,
    canDelete,
    url,
    itemsToAdd
}: ISidePanelListProps<T>) {

    useSignals();
    const [listItems, setListItems] = useState<T[] | undefined>(prePopulatedItems);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
    const [defaultFilters, setDefaultFilters] = useState([
        {
            propertyName: PropertyNames.entityName,
            value: EntityNames.packingBox,
            required: false,
            matchType: FilterMatchType.Equal,
            type: PropertyTypes.entityName,
        } as IFilter,
        {
            propertyName: PropertyNames.id,
            value: id,
            required: false,
            matchType: FilterMatchType.Equal,
            type: PropertyTypes.uuid,
        } as IFilter,
    ]);

    async function getData(filters: IFilter[], searchVal?: string) {
        if (prePopulatedItems && prePopulatedItems.length > 0) {
            return;
        }
        setLoading(true);

        const requestPayload = {
            filters: [...filters],
            take,
            keysets: [],
        };

        if (searchFilter && searchVal) {
            requestPayload.filters.push(searchFilter(searchVal));
        }

        const endpoint = url || `api/${entityName}/list`;
        const result = await httpPost(endpoint, requestPayload);
        // Handle different response structures - standard list returns data[entityName], custom endpoints may return data.Cases or data directly
        const resultListItems = result.data[entityName] || result.data.Cases || result.data;
        setListItems(resultListItems);
        if (onInitialListLoad) {
            onInitialListLoad(resultListItems);
        }
        setLoading(false);
    }

    function handleDeleteItem(item: T) {
        const itemName = itemDisplayName ? itemDisplayName(item) : SingularLowerCase(entityName);
        modalSignal.value = (
            <ConfirmModal
                heading={`Delete ${itemName}?`}
                information=""
                ContinueFunc={() => deleteItem(item)}
                type={ConfirmType.Destructive}
            />
        );
    }

    async function deleteItem(item: T) {
        const payload = { id: (item as any).id };
        // Use the item's entityName if available (e.g., from ListWithSpecimens), otherwise use the component's entityName
        const itemEntityName = (item as any).entityName || entityName;
        const result = await httpPost(`api/${itemEntityName}/delete`, payload);
        if (result.status === HttpStatusCode.Ok) {
            removeDeletedItem(item);
        }
    }

    function removeDeletedItem(item: T) {
        setListItems((prevData) => prevData!.filter((x) => (x as any).id !== (item as any).id));
        if (onItemRemoved) {
            onItemRemoved(item);
        }
    }

    function isBusy() {
        return globalSubmittingSignal.value.isBusy || loading;
    }

    async function handleKeyPress(event: any, searchVal: string | undefined) {
        if (event.key === "Enter") {
            await getData(defaultFilters, searchVal);
        }
    }

    function handleCanAdd() {
        if (onAddNewClicked) {
            onAddNewClicked();
        }
    }

    async function handleRefresh() {
        await getData(defaultFilters, searchValue);
    }

    function getCanAdd() {
        // Can add is used to override the default add condition
        if(canAdd === null || canAdd === undefined) {
            return true;
        }
        return canAdd;
    }

    function getCanEdit() {
        // Can edit is used to override the default edit condition
        if(canEdit === null || canEdit === undefined) {
            return true;
        }
        return canEdit;
    }

    function getEditFunction() {
        // Only return editFunction if canEdit is true
        if (!getCanEdit()) {
            return undefined;
        }
        return editFunction;
    }

    function getCanDelete() {
        // Can delete is used to override the default delete condition
        if(canDelete === null || canDelete === undefined) {
            return true;
        }
        return canDelete;
    }

    function getDeleteFunction() {
        // Only return delete function if canDelete is true
        if (!getCanDelete()) {
            return undefined;
        }
        return handleDeleteItem;
    }

    useEffect(() => {
        if (id) {
            const updatedFilters = defaultFilters.map((filter) =>
                filter.propertyName === PropertyNames.id
                    ? { ...filter, value: id }
                    : filter
            );
            setDefaultFilters(updatedFilters);
            setListItems([]);
            getData(updatedFilters);
            setSearchValue(undefined);
        }
    }, [id]);

    useEffect(() => {
        if (prePopulatedItems && prePopulatedItems.length > 0) {
            setListItems(prePopulatedItems);
            if (onInitialListLoad) {
                onInitialListLoad(prePopulatedItems);
            }
        }
    }, [prePopulatedItems])

    useEffect(() => {
        if (itemData) {
            setListItems((currentItems) => {
                const items = currentItems || [];
                const isNew = !items.some((x) => (x as any).id === (itemData as any).id);
                if (isNew) {
                    return [...items, itemData];
                } else {
                    return items.map((item) =>
                        (item as any).id === (itemData as any).id ? { ...item, ...itemData } : item
                    );
                }
            });
        }
    }, [itemData]);

    useEffect(() => {
        if (itemsToAdd && itemsToAdd.length > 0) {
            // Add items directly to the list
            setListItems((currentItems) => {
                const items = currentItems || [];
                const updatedData = [...items];
                itemsToAdd.forEach((newItem) => {
                    const isNew = !updatedData.some((x) => (x as any).id === (newItem as any).id);
                    if (isNew) {
                        updatedData.push(newItem);
                    } else {
                        const index = updatedData.findIndex((x) => (x as any).id === (newItem as any).id);
                        if (index !== -1) {
                            updatedData[index] = { ...updatedData[index], ...newItem };
                        }
                    }
                });
                return updatedData;
            });
        }
    }, [itemsToAdd]);

    return (
        <div className="side-panel-table__wrapper margin__top--s">
            <div className="display--flex flex__direction--row alignItems--center justifyContent--space-between padding__right--xxs">
                <div className={`packing-box-ranges__list-header ${isSmall ? 'packing-box-ranges__list-header--small' : ''}`}>
                    <p className="small">{displayName ?? capitalizeFirstLetter(entityName)}</p>
                    {headerWidget && headerWidget(listItems ?? [])}
                </div>
                <div className="display--flex gap--xxs">
                    <Button
                        isDisabled={isBusy()}
                        className={"button button--secondary button--with-icon button--extra-small"}
                        onPress={handleRefresh}>
                            <img src={IconRefresh}></img>
                        <p className="button-text button-text--small">Refresh</p>
                    </Button>
                    {onAddNewClicked && getCanAdd() &&
                        <Button
                            isDisabled={isBusy()}
                            className={"button button--secondary button--with-icon button--extra-small"}
                            onPress={handleCanAdd}>
                            <img src={IconPlus}></img>
                            <p className="button-text button-text--small">Add</p>
                        </Button>
                    }
                </div>
            </div>
            {searchFilter && <div className="filters__primary margin__top--xs display--block">
                <div className="margin__left--xxs margin__right--xxs">
                    <AdobeTextField
                        value={searchValue}
                        onChangeText={setSearchValue}
                        label={searchLabel}
                        visibleLabel={false}
                        icon={IconSearch}
                        required={false}
                        EnterKeyPress={(e: any, val: any) => handleKeyPress(e, val)}
                        disabled={isBusy()}
                    />
                </div>
            </div>}
            {informationBarWidget && informationBarWidget}
            {listItems && listItems.length > 0 && !listWidget && (
                <div className="padding__right--xxs margin__top--xxs">
                    <TableVirtuoso
                        className="side-panel-table"
                        style={{ height: fixedHeight }}
                        overscan={{ main: 100, reverse: 100 }}
                        data={listItems}
                        initialItemCount={listItems?.length}
                        fixedHeaderContent={() => <tr>{displayColumns}</tr>}
                        itemContent={(index, item) => renderRowContent(item, getEditFunction(), getDeleteFunction())}
                        fixedFooterContent={() => <tr></tr>}
                        components={{
                            TableFoot: () => (
                                <>
                                    {loading &&
                                        [...Array(loadingRowCount)].map((_, i) => (
                                            <tr key={i}>
                                                {[...Array(loadingRowCount)].map((_, j) => (
                                                    <td key={j}>
                                                        <div className="loaders loaders__list"></div>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                </>
                            ),
                        }}
                    />
                </div>
            )}
            {listWidget && listWidget}
        </div>
    );
}
